import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { getListingTransactionMode } from 'app/lib/listing';
import { useOneBonification, useRejectBonification } from 'app/network/bonification';
import { useOneListing } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import type { BonifyMessage } from 'app/types/chat';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import type { TFunction } from 'i18next';
const theme = createDSTheme();

interface BonificationMessageProps {
  message: BonifyMessage;
  listingId?: string;
  channelUrl: string;
}

type BonificationView =
  | 'borrower-prompt'
  | 'borrower-submitted'
  | 'lender-nudge'
  | 'lender-modified'
  | 'lender-review'
  | 'lender-reviewed'
  | 'hidden';

const getBonificationView = ({
  bonificationId,
  canBonify,
  hasListing,
  isBorrower,
  listingId,
  status,
}: {
  bonificationId: string | undefined;
  canBonify: boolean | undefined;
  hasListing: boolean;
  isBorrower: boolean;
  listingId: string | undefined;
  status: string | undefined;
}): BonificationView => {
  if (isBorrower && status === 'pending') return 'borrower-prompt';
  if (isBorrower && ['reviewing', 'accepted', 'rejected'].includes(status ?? '')) return 'borrower-submitted';
  if (isBorrower && !bonificationId) return 'borrower-prompt';
  if (!bonificationId && listingId) {
    if (!hasListing) return 'hidden';
    return canBonify ? 'lender-nudge' : 'lender-modified';
  }
  if (bonificationId && status === 'reviewing') return 'lender-review';
  if (status === 'accepted' || status === 'rejected') return 'lender-reviewed';
  return 'hidden';
};

const BorrowerPrompt = ({ onPress, t }: { onPress: () => void; t: TFunction }) => (
  <Column gap="spacing.general.x4" style={styles.block} width="100%">
    <Text fontSize={16} fontWeight="700" alignSelf="center">
      {t('bonification.borrowerPrompt.title')}
    </Text>
    <Text variant="bodyBase">{t('bonification.borrowerPrompt.text')}</Text>
    <Button marginTop="spacing.general.x2" onPress={onPress} variant="primary">
      {t('bonification.borrowerPrompt.cta')}
    </Button>
  </Column>
);

const BonificationMessageContent = ({
  borrowerName,
  isRejecting,
  onNavigateToBonification,
  onReject,
  status,
  t,
  transactionMode,
  view,
}: {
  borrowerName: string | undefined;
  isRejecting: boolean;
  onNavigateToBonification: () => void;
  onReject: () => void;
  status: string | undefined;
  t: TFunction;
  transactionMode: string;
  view: BonificationView;
}) => {
  switch (view) {
    case 'borrower-prompt':
      return <BorrowerPrompt onPress={onNavigateToBonification} {...{ t }} />;
    case 'borrower-submitted':
      return (
        <Column gap="spacing.general.x4" style={styles.block} width="100%">
          <Text fontSize={16} fontWeight="700" alignSelf="center">
            {t(`bonification.borrowerSubmitted.title.${status ?? 'pending'}`)}
          </Text>
          <Text variant="bodyBase">{t(`bonification.borrowerSubmitted.text.${status ?? 'pending'}`)}</Text>
        </Column>
      );
    case 'lender-nudge':
      return (
        <Column gap="spacing.general.x4" style={styles.block} width="100%">
          <Text fontSize={16} fontWeight="700" alignSelf="center">
            {t(`bonification.lenderNudge.title.${transactionMode}`)}
          </Text>
          <Text variant="bodyBase">{t('bonification.lenderNudge.text')}</Text>
          <Button marginTop="spacing.general.x2" onPress={onNavigateToBonification} variant="primary">
            {t('bonification.lenderNudge.cta')}
          </Button>
        </Column>
      );
    case 'lender-modified':
      return (
        <Column gap="spacing.general.x4" style={styles.block} width="100%">
          <Text fontSize={16} fontWeight="700" alignSelf="center">
            {t('bonification.lenderReviewed.title.modified')}
          </Text>
          <Text variant="bodyBase">{t('bonification.lenderReviewed.text.modified')}</Text>
        </Column>
      );
    case 'lender-review':
      return (
        <Column gap="spacing.general.x4" style={styles.block} width="100%">
          <Text fontSize={16} fontWeight="700" alignSelf="center">
            {t('bonification.lenderReview.title')}
          </Text>
          <Text variant="bodyBase">
            <Trans
              ns="one-channel"
              i18nKey="bonification.lenderReview.text1"
              values={{ name: borrowerName }}
              components={{ strong: <Text fontWeight="700" /> }}
            />
          </Text>
          <Text variant="bodyBase">{t('bonification.lenderReview.text2')}</Text>
          <Button marginTop="spacing.general.x2" onPress={onNavigateToBonification} variant="primary">
            <Text color="surface.default">{t('bonification.lenderReview.ctaView')}</Text>
          </Button>
          <Button disabled={isRejecting} onPress={onReject} variant="plain">
            <Text color="text.primary" textDecorationLine="underline">
              {t('bonification.lenderReview.ctaReject')}
            </Text>
          </Button>
        </Column>
      );
    case 'lender-reviewed':
      return (
        <Column gap="spacing.general.x4" style={styles.block} width="100%">
          <Text fontSize={16} fontWeight="700" alignSelf="center">
            {t(`bonification.lenderReviewed.title.${status ?? 'rejected'}`)}
          </Text>
          <Text variant="bodyBase">{t(`bonification.lenderReviewed.text.${status ?? 'rejected'}`)}</Text>
        </Column>
      );
    case 'hidden':
      return null;
    default:
      return null;
  }
};

export function BonificationMessage({ message, listingId: lid, channelUrl }: BonificationMessageProps) {
  const { t } = useTranslation('one-channel');
  const { data: user } = useUser();
  const navigation = useNavigation();

  const listingId = lid ?? message.data.listingId;
  const listing = useOneListing(listingId, 'one-channel').data;
  const transactionMode = getListingTransactionMode(listing);

  const isBorrower =
    (message.loan && 'borrowerId' in message.loan && user?.id === message.loan.borrowerId) ||
    user?.id !== listing?.userId;
  const bonificationId = message.data.bonificationId;

  const { data: bonification } = useOneBonification(bonificationId);
  const { mutate: reject, isPending: isRejecting } = useRejectBonification();

  const status = bonification?.status;

  const navigateToBonification = () => {
    navigation.navigate(
      'listing-bonification',
      {
        loanId: message.data.loanId,
        bonificationId: bonificationId,
        listingId: listingId,
      },
      { pop: true },
    );
  };

  const view = getBonificationView({
    bonificationId,
    canBonify: listing?.canBonify,
    hasListing: Boolean(listing),
    isBorrower,
    listingId,
    status,
  });

  return (
    <BonificationMessageContent
      {...{ isRejecting, status, t, transactionMode, view }}
      borrowerName={message.loan?.borrower.profile?.firstName}
      onNavigateToBonification={
        view === 'lender-nudge' ? () => navigateToListing(navigation, listingId) : navigateToBonification
      }
      onReject={() => bonificationId && reject({ id: bonificationId, channelUrl })}
    />
  );
}

const styles = StyleSheet.create({
  block: {
    backgroundColor: theme.colors['surface.primary-neutral'],
    borderRadius: theme.borderRadii['radius.lg'],
    padding: 20,
  },
});
